import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Message } from '../../../../classes/Message';
import { ITaskDelayFailure } from '../../../../interfaces/delay-failure/task-delay-failure';
import {
  IDelayFailureTask,
  IDelayFailureTemplate,
} from '../../../../interfaces/delay-failure/delay-failure-template';
import { AutoComplete, MessageService, SelectItem } from 'primeng';
import { IDelayFailureCategory } from '../../../../interfaces/delay-failure/delay-failure-category';
import { DelayFailureService, EventService } from '../../../../services';
import { DelayFailureAccountabilityEnum } from '../../../../enums/delay-failure-accountability.enum';

interface AutoCompleteCompleteEvent {
  originalEvent: Event;
  query: string;
}

@Component({
  templateUrl: './job-screen-delay-failures-dialog.component.html',
  selector: 'job-screen-delay-failures-dialog',
  // styleUrls: ['./job-screen-job-details.component.scss', '../job-screen-common.scss'],
})
export class JobScreenDelayFailuresDialogComponent implements OnInit, OnChanges {
  @Input() visible: boolean = false;

  @Input() taskId: number;

  @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output() onCreated: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('autocomplete') autocomplete: AutoComplete;

  public newDelayFailure: string;

  public newDelayFailureCategory: IDelayFailureCategory;

  public delayFailureCategories: SelectItem[];

  public shouldSendEmail = true;

  //The users that are assigned to this job
  private userEmails: string[] = [];

  public emailAddressSuggestions: string[] = [];

  public selectedEmailAddresses: string[] = [];

  public task: IDelayFailureTask;

  //email regex
  private emailRegExp = new RegExp(
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  );
  private emailRegExpCommas = new RegExp(
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,})),$/,
  );

  constructor(
    private messageService: MessageService,
    private eventService: EventService,
    private delayFailureService: DelayFailureService,
  ) {}

  ngOnInit(): void {
    void this.getDelayFailureCategories();
  }

  ngOnChanges(changes: SimpleChanges) {}

  onShow() {
    void this.getTask();
    void this.setupDelayFailureEmailList();
  }

  private async getDelayFailureCategories() {
    const result = await this.delayFailureService.getDelayFailureCategories(true);
    this.delayFailureCategories = [{ label: 'Select Category', value: null }];
    result.forEach((x) => {
      this.delayFailureCategories.push({
        label: `${x.category} (${DelayFailureAccountabilityEnum[x.accountable]})`,
        value: x,
      });
    });
  }

  private async getTask(): Promise<void> {
    this.task = await this.delayFailureService.getDelayFailureTask(this.taskId);
  }

  private async setupDelayFailureEmailList() {
    const result = await this.delayFailureService.getDelayFailureEmails(this.taskId);

    this.userEmails = result.userEmails;
    this.selectedEmailAddresses = result.preSelectedEmails;
  }

  saveDelayFailure() {
    if (!this.newDelayFailureCategory) {
      this.messageService.add(Message.Error('No delay/failure reason selected'));
      return;
    }

    const dto = <ITaskDelayFailure>{
      taskId: this.taskId,
      delayFailureCategoryId: this.newDelayFailureCategory.id,
      delayFailureTemplate: this.shouldSendEmail
        ? this.setupDelayFailureTemplate(this.newDelayFailure)
        : undefined,
    };

    this.delayFailureService.addTaskDelayFailure(dto).then(() => {
      this.resetForm();
      this.onCreated.emit();
    });
  }

  resetForm() {
    this.newDelayFailure = null;
    this.newDelayFailureCategory = null;
    this.selectedEmailAddresses = [];
    this.shouldSendEmail = true;
  }

  onDelayFailureCategoryChange() {
    if (!this.newDelayFailureCategory.delayFailureTemplate) {
      return;
    }

    this.delayFailureService
      .getRenderedDelayFailureTemplate(
        this.taskId,
        this.newDelayFailureCategory.delayFailureTemplateId,
      )
      .then((delayTemplate) => {
        this.newDelayFailure = delayTemplate.bodyTemplate;
      });
  }

  filterEmailAddress(event: AutoCompleteCompleteEvent) {
    const validEmail = this.emailRegExp.test(event.query);

    if (validEmail) {
      this.emailAddressSuggestions = [event.query, ...Object.values(this.userEmails)];
    } else {
      this.emailAddressSuggestions = Object.values(this.userEmails);
    }
    this.autoMarkEmail(event.query);
  }

  autoMarkEmail(email: string) {
    const validEnteredEmail = this.emailRegExpCommas.test(email);

    if (!validEnteredEmail) {
      return;
    }

    // Unfortunately, there isn't a way to get the clear the input of autocomplete, so we have to find it via
    // child element
    const newEmail = email.slice(0, -1);
    this.selectedEmailAddresses.push(newEmail);
    const ulElement = this.autocomplete.el.nativeElement?.childNodes[0]?.children[0];
    if (ulElement) {
      ulElement.lastChild.lastChild.value = '';
    }
  }

  private setupDelayFailureTemplate(newNotes: string): IDelayFailureTemplate {
    if (!this.newDelayFailureCategory.delayFailureTemplate) {
      return;
    }

    this.newDelayFailureCategory.delayFailureTemplate.bodyTemplate = newNotes;

    this.newDelayFailureCategory.delayFailureTemplate.recipientAddressTemplate =
      this.selectedEmailAddresses.join(',');

    return this.newDelayFailureCategory.delayFailureTemplate;
  }
}
